import { isAxiosError } from 'axios';

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogMeta {
  source?: string;
  error?: unknown;
  [key: string]: any;
}

const formatError = (err: unknown): Record<string, any> => {
  if (isAxiosError(err)) {
    return {
      name: err.name,
      message: err.message,
      status: err.response?.status,
      data: err.response?.data,
      stack: err.stack,
    };
  }
  if (err instanceof Error) {
    return {
      name: err.name,
      message: err.message,
      stack: err.stack,
    };
  }
  return { error: err };
};

const log = (level: LogLevel, message: string, meta?: LogMeta) => {
  const timestamp = new Date().toISOString();
  const logEntry: Record<string, any> = {
    timestamp,
    level: level.toUpperCase(),
    message,
  };

  if (meta?.source) logEntry.source = meta.source;

  if (meta?.error) {
    logEntry.error = formatError(meta.error);
  }

  // Add any other meta props
  Object.entries(meta || {}).forEach(([key, value]) => {
    if (key !== 'source' && key !== 'error') {
      logEntry[key] = value;
    }
  });

  const output = JSON.stringify(logEntry);

  switch (level) {
    case 'info':
      console.info(output);
      break;
    case 'warn':
      console.warn(output);
      break;
    case 'error':
      console.error(output);
      break;
    case 'debug':
      if (process.env.NODE_ENV !== 'production') {
        console.debug(output);
      }
      break;
    default:
      console.log(output);
      break;
  }
};

export const logger = {
  info: (msg: string, meta?: LogMeta) => log('info', msg, meta),
  warn: (msg: string, meta?: LogMeta) => log('warn', msg, meta),
  error: (msg: string, meta?: LogMeta) => log('error', msg, meta),
  debug: (msg: string, meta?: LogMeta) => log('debug', msg, meta),
};
